/**
 * SizilPro — BrandColors
 * Step 2: Logo upload, richer brand controls, color theme, font preference.
 *
 * Updated toward the guided setup flow while keeping the current save contract
 * and existing setup route structure intact.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import {
  UploadCloud,
  Loader2,
  CheckCircle2,
  X,
  ImagePlus,
  Palette,
  Type,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useBrandSettings,
  useUploadLogo,
  useUpsertBrandSettings,
  useUploadBrandAsset,
  useUploadBrandFont,
  useBrandAssets,
  useBrandFonts,
} from "../../hooks/useBrandSettings";
import type { BrandAsset, BrandFont } from "../../types";
import {
  SetupActions,
  SetupFieldGroup,
  SetupLabel,
} from "./SetupPrimitives";

type BuiltInFontPreference = "inter" | "roboto" | "lato" | "open-sans";
type FontRole = "heading" | "body";
type FontSource = "built_in" | "uploaded";

type TypographySelection = {
  source: FontSource;
  builtInFont: BuiltInFontPreference | null;
  uploadedFontId: string | null;
  uploadedFontName: string | null;
};

type StoredTypographyPreferences = {
  heading?: Partial<TypographySelection>;
  body?: Partial<TypographySelection>;
};

const FONTS: {
  id: BuiltInFontPreference;
  label: string;
  sample: string;
}[] = [
  { id: "inter", label: "Inter", sample: "font-sans" },
  { id: "roboto", label: "Roboto", sample: "font-sans" },
  { id: "lato", label: "Lato", sample: "font-sans" },
  { id: "open-sans", label: "Open Sans", sample: "font-sans" },
];

const BUILT_IN_FONT_LABELS: Record<BuiltInFontPreference, string> = {
  inter: "Inter",
  roboto: "Roboto",
  lato: "Lato",
  "open-sans": "Open Sans",
};

const PRESET_COLORS = [
  { label: "Midnight", primary: "#0f0f0f", accent: "#FF6B00", secondary: "#1F2937" },
  { label: "Navy", primary: "#1e3a5f", accent: "#F59E0B", secondary: "#2b587f" },
  { label: "Forest", primary: "#1a3d2b", accent: "#34D399", secondary: "#365f49" },
  { label: "Slate", primary: "#334155", accent: "#38BDF8", secondary: "#475569" },
  { label: "Crimson", primary: "#7f1d1d", accent: "#FCA5A5", secondary: "#991b1b" },
  { label: "Plum", primary: "#3b1f5e", accent: "#C084FC", secondary: "#5b2d86" },
  { label: "Espresso", primary: "#2c1a0e", accent: "#FBBF24", secondary: "#4a2f1f" },
  { label: "Charcoal", primary: "#1f2937", accent: "#6EE7B7", secondary: "#374151" },
  { label: "Dusk", primary: "#2d2b55", accent: "#F472B6", secondary: "#434190" },
  { label: "Sage", primary: "#2d3d2f", accent: "#BEF264", secondary: "#415843" },
  { label: "Rust", primary: "#431407", accent: "#FB923C", secondary: "#7c2d12" },
  { label: "Arctic", primary: "#0c2340", accent: "#67E8F9", secondary: "#1d4e89" },
];

interface Props {
  onComplete: () => void;
}

function formatEnumLabel(value: string) {
  return value.replace(/_/g, " ");
}

function QuestionCard({
  number,
  title,
  description,
  children,
}: {
  number: number;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
      <div className="mb-5 flex items-start gap-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-sm font-semibold text-black">
          {number}
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          {description ? (
            <p className="mt-1 text-sm leading-6 text-white/45">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function AssetThumb({ asset }: { asset: BrandAsset }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-2">
      <div className="flex h-20 items-center justify-center overflow-hidden rounded-lg bg-white/5">
        {asset.public_url ? (
          <img
            src={asset.public_url}
            alt={asset.alt_text || asset.name}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <div className="text-xs text-white/35">{asset.name}</div>
        )}
      </div>
      <div className="mt-2">
        <p className="truncate text-xs font-medium text-white/80">{asset.name}</p>
        <p className="text-[10px] uppercase tracking-[0.12em] text-white/35">
          {formatEnumLabel(asset.asset_role)}
        </p>
      </div>
    </div>
  );
}

function createDefaultSelection(
  builtInFont: BuiltInFontPreference = "inter"
): TypographySelection {
  return {
    source: "built_in",
    builtInFont,
    uploadedFontId: null,
    uploadedFontName: null,
  };
}

function coerceBuiltInFont(value: unknown): BuiltInFontPreference | null {
  return value === "inter" ||
    value === "roboto" ||
    value === "lato" ||
    value === "open-sans"
    ? value
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeStoredSelection(
  input: unknown,
  fallbackBuiltIn: BuiltInFontPreference
): TypographySelection {
  if (!isRecord(input)) {
    return createDefaultSelection(fallbackBuiltIn);
  }

  const source: FontSource =
    input.source === "uploaded" ? "uploaded" : "built_in";

  const builtInFont =
    coerceBuiltInFont(input.builtInFont) ?? fallbackBuiltIn;

  const uploadedFontId =
    typeof input.uploadedFontId === "string" ? input.uploadedFontId : null;

  const uploadedFontName =
    typeof input.uploadedFontName === "string" ? input.uploadedFontName : null;

  if (source === "uploaded" && uploadedFontId) {
    return {
      source,
      builtInFont,
      uploadedFontId,
      uploadedFontName,
    };
  }

  return {
    source: "built_in",
    builtInFont,
    uploadedFontId: null,
    uploadedFontName: null,
  };
}

function getTypographySelections(
  brand: {
    font_preference?: string | null;
    preview_preferences?: Record<string, unknown> | null;
  } | null | undefined
): {
  heading: TypographySelection;
  body: TypographySelection;
} {
  const fallback = coerceBuiltInFont(brand?.font_preference) ?? "inter";
  const storedTypography = isRecord(brand?.preview_preferences?.typography)
    ? (brand?.preview_preferences?.typography as StoredTypographyPreferences)
    : undefined;

  return {
    heading: normalizeStoredSelection(storedTypography?.heading, fallback),
    body: normalizeStoredSelection(storedTypography?.body, fallback),
  };
}

function getSelectionLabel(
  selection: TypographySelection,
  uploadedFonts: BrandFont[]
) {
  if (selection.source === "uploaded" && selection.uploadedFontId) {
    const match = uploadedFonts.find((font) => font.id === selection.uploadedFontId);
    return match?.family_name || selection.uploadedFontName || "Uploaded font";
  }

  const builtIn = selection.builtInFont ?? "inter";
  return BUILT_IN_FONT_LABELS[builtIn];
}

function FontRolePicker({
  role,
  selection,
  uploadedFonts,
  onSelectBuiltIn,
  onSelectUploaded,
}: {
  role: FontRole;
  selection: TypographySelection;
  uploadedFonts: BrandFont[];
  onSelectBuiltIn: (role: FontRole, font: BuiltInFontPreference) => void;
  onSelectUploaded: (role: FontRole, font: BrandFont) => void;
}) {
  const roleTitle = role === "heading" ? "Heading font" : "Body font";
  const roleDescription =
    role === "heading"
      ? "Used for proposal titles, section headers, and stronger type moments."
      : "Used for paragraphs, supporting copy, and the main reading experience.";

  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <div className="mb-4">
        <p className="text-sm font-semibold text-white/85">{roleTitle}</p>
        <p className="mt-1 text-xs leading-6 text-white/40">{roleDescription}</p>
      </div>

      <div className="space-y-4">
        <div>
          <p className="mb-2 text-[11px] uppercase tracking-[0.16em] text-white/35">
            Built-in fonts
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {FONTS.map((fontOption) => {
              const isSelected =
                selection.source === "built_in" &&
                selection.builtInFont === fontOption.id;

              return (
                <button
                  key={`${role}-${fontOption.id}`}
                  type="button"
                  onClick={() => onSelectBuiltIn(role, fontOption.id)}
                  className={cn(
                    "flex flex-col gap-1 rounded-lg border px-4 py-3 text-left transition-all",
                    isSelected
                      ? "border-white/40 bg-white/10"
                      : "border-white/10 hover:border-white/20 hover:bg-white/5"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{fontOption.label}</span>
                    {isSelected ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    ) : null}
                  </div>
                  <span className={cn("text-xs text-white/40", fontOption.sample)}>
                    Aa Bb Cc 123
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="mb-2 text-[11px] uppercase tracking-[0.16em] text-white/35">
            Uploaded fonts
          </p>

          {uploadedFonts.length ? (
            <div className="grid gap-2">
              {uploadedFonts.map((uploadedFont) => {
                const isSelected =
                  selection.source === "uploaded" &&
                  selection.uploadedFontId === uploadedFont.id;

                return (
                  <button
                    key={`${role}-${uploadedFont.id}`}
                    type="button"
                    onClick={() => onSelectUploaded(role, uploadedFont)}
                    className={cn(
                      "flex items-center justify-between rounded-lg border px-4 py-3 text-left transition-all",
                      isSelected
                        ? "border-white/40 bg-white/10"
                        : "border-white/10 bg-black/20 hover:border-white/20 hover:bg-white/5"
                    )}
                  >
                    <div>
                      <p className="text-sm font-medium text-white/85">
                        {uploadedFont.family_name}
                      </p>
                      <p className="mt-1 text-xs text-white/40">
                        Uploaded font • {uploadedFont.file_format?.toUpperCase() ?? "FILE"}
                      </p>
                    </div>
                    {isSelected ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-white/40">
              No custom fonts uploaded yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function BrandingSummaryCard({
  showLogo,
  useCustomColors,
  headingFontLabel,
  bodyFontLabel,
}: {
  showLogo: boolean;
  useCustomColors: boolean;
  headingFontLabel: string;
  bodyFontLabel: string;
}) {
  return (
    <QuestionCard
      number={5}
      title="How should the brand feel in the proposal?"
      description="This is a quick summary of the direction you’re creating in the live preview."
    >
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="mb-2 inline-flex rounded-full bg-white/10 p-2">
            <UploadCloud className="h-4 w-4 text-white/70" />
          </div>
          <p className="text-sm font-medium text-white/85">
            {showLogo ? "Logo-forward" : "Minimal branding"}
          </p>
          <p className="mt-2 text-xs leading-6 text-white/40">
            {showLogo
              ? "Your logo becomes a visible part of the cover and brand surfaces."
              : "The proposal leans cleaner and more typography-first."}
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="mb-2 inline-flex rounded-full bg-white/10 p-2">
            <Palette className="h-4 w-4 text-white/70" />
          </div>
          <p className="text-sm font-medium text-white/85">
            {useCustomColors ? "Advanced palette" : "Preset palette"}
          </p>
          <p className="mt-2 text-xs leading-6 text-white/40">
            {useCustomColors
              ? "You’re controlling more of the proposal’s visual system."
              : "You’re using a faster preset style that still looks polished."}
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="mb-2 inline-flex rounded-full bg-white/10 p-2">
            <Type className="h-4 w-4 text-white/70" />
          </div>
          <p className="text-sm font-medium text-white/85">{headingFontLabel}</p>
          <p className="mt-2 text-xs leading-6 text-white/40">
            Heading font for major titles and section labels.
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="mb-2 inline-flex rounded-full bg-white/10 p-2">
            <Type className="h-4 w-4 text-white/70" />
          </div>
          <p className="text-sm font-medium text-white/85">{bodyFontLabel}</p>
          <p className="mt-2 text-xs leading-6 text-white/40">
            Body font for paragraphs and main reading content.
          </p>
        </div>
      </div>
    </QuestionCard>
  );
}

export function BrandColors({ onComplete }: Props) {
  const { data: brand } = useBrandSettings();
  const { data: brandAssets = [] } = useBrandAssets();
  const { data: brandFonts = [] } = useBrandFonts();

  const uploadLogo = useUploadLogo();
  const uploadBrandAsset = useUploadBrandAsset();
  const uploadBrandFont = useUploadBrandFont();
  const upsert = useUpsertBrandSettings();

  const [primaryColor, setPrimaryColor] = useState("#0f0f0f");
  const [secondaryColor, setSecondaryColor] = useState("#1F2937");
  const [accentColor, setAccentColor] = useState("#FF6B00");
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [textColor, setTextColor] = useState("#111827");
  const [mutedColor, setMutedColor] = useState("#6B7280");

  const [headingFont, setHeadingFont] = useState<TypographySelection>(
    createDefaultSelection("inter")
  );
  const [bodyFont, setBodyFont] = useState<TypographySelection>(
    createDefaultSelection("inter")
  );

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [isRemovingLogo, setIsRemovingLogo] = useState(false);
  const [showLogo, setShowLogo] = useState(true);
  const [useCustomColors, setUseCustomColors] = useState(false);

  const logoFileRef = useRef<HTMLInputElement>(null);
  const extraBrandingRef = useRef<HTMLInputElement>(null);
  const fontUploadRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!brand) return;

    setPrimaryColor(brand.primary_color ?? "#0f0f0f");
    setSecondaryColor(brand.secondary_color ?? "#1F2937");
    setAccentColor(brand.accent_color ?? "#FF6B00");
    setBackgroundColor(brand.background_color ?? "#FFFFFF");
    setTextColor(brand.text_color ?? "#111827");
    setMutedColor(brand.muted_color ?? "#6B7280");

    const typography = getTypographySelections(brand);
    setHeadingFont(typography.heading);
    setBodyFont(typography.body);

    setLogoPreview(brand.logo_url ?? null);
    setShowLogo(brand.show_logo ?? true);
    setUseCustomColors(brand.use_custom_colors ?? false);
  }, [brand]);

  const extraAssets = useMemo(
    () =>
      brandAssets.filter(
        (asset) =>
          asset.asset_role !== "primary_logo" &&
          asset.asset_role !== "secondary_logo"
      ),
    [brandAssets]
  );

  const uploadedFonts = useMemo(
    () => brandFonts.filter((font) => font.source_type === "uploaded"),
    [brandFonts]
  );

  const headingFontLabel = useMemo(
    () => getSelectionLabel(headingFont, uploadedFonts),
    [headingFont, uploadedFonts]
  );

  const bodyFontLabel = useMemo(
    () => getSelectionLabel(bodyFont, uploadedFonts),
    [bodyFont, uploadedFonts]
  );

  const handleSelectBuiltInFont = (
    role: FontRole,
    builtInFont: BuiltInFontPreference
  ) => {
    const nextSelection: TypographySelection = {
      source: "built_in",
      builtInFont,
      uploadedFontId: null,
      uploadedFontName: null,
    };

    if (role === "heading") {
      setHeadingFont(nextSelection);
      return;
    }

    setBodyFont(nextSelection);
  };

  const handleSelectUploadedFont = (role: FontRole, uploadedFont: BrandFont) => {
    const fallbackBuiltIn =
      role === "heading"
        ? headingFont.builtInFont ?? "inter"
        : bodyFont.builtInFont ?? "inter";

    const nextSelection: TypographySelection = {
      source: "uploaded",
      builtInFont: fallbackBuiltIn,
      uploadedFontId: uploadedFont.id,
      uploadedFontName: uploadedFont.family_name,
    };

    if (role === "heading") {
      setHeadingFont(nextSelection);
      return;
    }

    setBodyFont(nextSelection);
  };

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) return;

    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);

    try {
      const uploadedUrl = await uploadLogo.mutateAsync(file);
      setLogoPreview(uploadedUrl);
      setShowLogo(true);
    } catch {
      setLogoPreview(brand?.logo_url ?? null);
    } finally {
      URL.revokeObjectURL(preview);
    }
  };

  const handleExtraBrandingUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) return;

    await uploadBrandAsset.mutateAsync({
      file,
      asset: {
        name: file.name,
        asset_role: "badge",
        alt_text: file.name,
        width: null,
        height: null,
        placement_preset: "top_right",
        page_scope: "all",
        is_active: true,
        sort_order: extraAssets.length,
        metadata: {},
      },
    });
  };

  const handleFontUpload = async (file: File) => {
    const createdFont = await uploadBrandFont.mutateAsync({
      file,
      font: {
        family_name: file.name.replace(/\.[^/.]+$/, ""),
        font_weight: 400,
        font_style: "normal",
        is_active: true,
        is_default: true,
        metadata: {},
      },
      setAsDefault: true,
    });

    setBodyFont({
      source: "uploaded",
      builtInFont: bodyFont.builtInFont ?? "inter",
      uploadedFontId: createdFont.id,
      uploadedFontName: createdFont.family_name,
    });
  };

  const handleRemoveLogo = async () => {
    setIsRemovingLogo(true);
    const previous = logoPreview;
    setLogoPreview(null);

    try {
      await upsert.mutateAsync({
        logo_url: null,
        logo_original_url: null,
        show_logo: false,
        default_logo_asset_id: null,
      });
      setShowLogo(false);
    } catch {
      setLogoPreview(previous);
    } finally {
      setIsRemovingLogo(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  };

  const handleSave = async () => {
    const nextPreviewPreferences = {
      ...(brand?.preview_preferences ?? {}),
      typography: {
        heading: headingFont,
        body: bodyFont,
      },
    };

    await upsert.mutateAsync({
      primary_color: primaryColor,
      secondary_color: secondaryColor,
      accent_color: accentColor,
      background_color: backgroundColor,
      text_color: textColor,
      muted_color: mutedColor,

      font_preference:
        bodyFont.source === "built_in" && bodyFont.builtInFont
          ? bodyFont.builtInFont
          : headingFont.source === "built_in" && headingFont.builtInFont
            ? headingFont.builtInFont
            : "inter",

      default_font_id:
        bodyFont.source === "uploaded"
          ? bodyFont.uploadedFontId
          : headingFont.source === "uploaded"
            ? headingFont.uploadedFontId
            : null,

      show_logo: showLogo,
      use_custom_colors: useCustomColors,
      color_palette: {
        primary: primaryColor,
        secondary: secondaryColor,
        accent: accentColor,
        background: backgroundColor,
        text: textColor,
        muted: mutedColor,
      },
      preview_preferences: nextPreviewPreferences,
    });

    onComplete();
  };

  const isSaving =
    upsert.isPending ||
    isRemovingLogo ||
    uploadLogo.isPending ||
    uploadBrandAsset.isPending ||
    uploadBrandFont.isPending;

  return (
    <div className="space-y-6">
      <div className="mb-2">
        <p className="text-sm leading-6 text-white/55">
          Now let’s shape how your proposal looks. This step controls your logo,
          brand colors, supporting graphics, and typography.
        </p>
      </div>

      <div className="rounded-2xl border border-amber-500/15 bg-amber-500/5 p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div>
            <p className="text-sm font-medium text-amber-300">
              This step drives the live preview
            </p>
            <p className="mt-1 text-sm leading-6 text-white/45">
              As you save this step, the right-side preview becomes more like the
              proposal your users will eventually send as a PDF.
            </p>
          </div>
        </div>
      </div>

      <QuestionCard
        number={1}
        title="Do you want your main logo to appear on your proposal?"
        description="Transparent PNG or SVG works best. You can hide the logo for a cleaner minimal style."
      >
        <div className="mb-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowLogo(true)}
            className={cn(
              "rounded-lg border px-4 py-2 text-sm font-medium transition-all",
              showLogo
                ? "border-white/40 bg-white/10 text-white"
                : "border-white/10 text-white/55 hover:border-white/20 hover:text-white"
            )}
          >
            Yes, show logo
          </button>
          <button
            type="button"
            onClick={() => setShowLogo(false)}
            className={cn(
              "rounded-lg border px-4 py-2 text-sm font-medium transition-all",
              !showLogo
                ? "border-white/40 bg-white/10 text-white"
                : "border-white/10 text-white/55 hover:border-white/20 hover:text-white"
            )}
          >
            No, keep it minimal
          </button>
        </div>

        {showLogo ? (
          <SetupFieldGroup>
            <p className="mb-3 text-xs text-white/40">
              Appears on the cover and brand areas of your proposal.
            </p>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => logoFileRef.current?.click()}
              className={cn(
                "relative flex h-36 cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed transition-all",
                dragOver
                  ? "border-white/40 bg-white/5"
                  : "border-white/15 hover:border-white/25 hover:bg-white/[0.03]"
              )}
            >
              {logoPreview ? (
                <>
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-h-16 max-w-48 object-contain"
                  />

                  {(uploadLogo.isPending || isRemovingLogo) && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/60">
                      <Loader2 className="h-6 w-6 animate-spin text-white" />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleRemoveLogo();
                    }}
                    className="absolute right-2 top-2 rounded-md bg-white/10 p-1 transition-colors hover:bg-white/20"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <UploadCloud className="h-8 w-8 text-white/30" />
                  <div className="text-center">
                    <p className="text-sm text-white/60">
                      Drop your logo here or{" "}
                      <span className="text-white/90 underline underline-offset-2">
                        click to upload
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-white/30">
                      PNG, SVG, JPG — transparent background recommended
                    </p>
                  </div>
                </>
              )}
            </div>

            <input
              ref={logoFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
          </SetupFieldGroup>
        ) : (
          <div className="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-white/65">
            Your proposal preview will use a cleaner text-first brand style without a main logo.
          </div>
        )}
      </QuestionCard>

      <QuestionCard
        number={2}
        title="Do you have any other logos or branding you want to show?"
        description="Use this for badges, partner marks, certifications, or secondary branding elements."
      >
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => extraBrandingRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/20 hover:bg-white/10"
          >
            <ImagePlus className="h-4 w-4" />
            Upload extra branding
          </button>
          {uploadBrandAsset.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin text-white/50" />
          ) : null}
        </div>

        <input
          ref={extraBrandingRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleExtraBrandingUpload(file);
          }}
        />

        {extraAssets.length ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {extraAssets.map((asset) => (
              <AssetThumb key={asset.id} asset={asset} />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-white/45">
            No extra branding uploaded yet.
          </div>
        )}
      </QuestionCard>

      <QuestionCard
        number={3}
        title="What are your brand colors?"
        description="Choose a preset or turn on advanced colors for deeper control over the proposal preview and final PDF styling."
      >
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setUseCustomColors(false)}
            className={cn(
              "rounded-lg border px-4 py-2 text-sm font-medium transition-all",
              !useCustomColors
                ? "border-white/40 bg-white/10 text-white"
                : "border-white/10 text-white/55 hover:border-white/20 hover:text-white"
            )}
          >
            Use preset colors
          </button>
          <button
            type="button"
            onClick={() => setUseCustomColors(true)}
            className={cn(
              "rounded-lg border px-4 py-2 text-sm font-medium transition-all",
              useCustomColors
                ? "border-white/40 bg-white/10 text-white"
                : "border-white/10 text-white/55 hover:border-white/20 hover:text-white"
            )}
          >
            Advanced custom colors
          </button>
        </div>

        <SetupFieldGroup label="Color theme">
          <p className="mb-3 text-xs text-white/40">
            Primary drives major surfaces. Accent handles highlights. Secondary,
            text, muted, and background help create a cleaner polished proposal.
          </p>

          <div className="mb-4 flex flex-wrap gap-2">
            {PRESET_COLORS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  setPrimaryColor(preset.primary);
                  setSecondaryColor(preset.secondary);
                  setAccentColor(preset.accent);
                  setBackgroundColor("#FFFFFF");
                  setTextColor("#111827");
                  setMutedColor("#6B7280");
                }}
                title={preset.label}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all",
                  primaryColor === preset.primary
                    ? "border-white/40 bg-white/10 text-white"
                    : "border-white/10 text-white/50 hover:border-white/20 hover:text-white/70"
                )}
              >
                <span
                  className="h-3.5 w-3.5 rounded-full border border-white/20"
                  style={{ background: preset.primary }}
                />
                {preset.label}
              </button>
            ))}
          </div>

          <div className={cn("grid gap-4", useCustomColors ? "md:grid-cols-3" : "md:grid-cols-2")}>
            <div>
              <SetupLabel htmlFor="primary_color">Primary color</SetupLabel>
              <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5">
                <input
                  id="primary_color"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                />
                <span className="font-mono text-sm text-white/70">{primaryColor}</span>
              </div>
            </div>

            <div>
              <SetupLabel htmlFor="accent_color">Accent color</SetupLabel>
              <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5">
                <input
                  id="accent_color"
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                />
                <span className="font-mono text-sm text-white/70">{accentColor}</span>
              </div>
            </div>

            {useCustomColors ? (
              <>
                <div>
                  <SetupLabel htmlFor="secondary_color">Secondary color</SetupLabel>
                  <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5">
                    <input
                      id="secondary_color"
                      type="color"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                    />
                    <span className="font-mono text-sm text-white/70">{secondaryColor}</span>
                  </div>
                </div>

                <div>
                  <SetupLabel htmlFor="background_color">Background color</SetupLabel>
                  <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5">
                    <input
                      id="background_color"
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                    />
                    <span className="font-mono text-sm text-white/70">{backgroundColor}</span>
                  </div>
                </div>

                <div>
                  <SetupLabel htmlFor="text_color">Text color</SetupLabel>
                  <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5">
                    <input
                      id="text_color"
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                    />
                    <span className="font-mono text-sm text-white/70">{textColor}</span>
                  </div>
                </div>

                <div>
                  <SetupLabel htmlFor="muted_color">Muted color</SetupLabel>
                  <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5">
                    <input
                      id="muted_color"
                      type="color"
                      value={mutedColor}
                      onChange={(e) => setMutedColor(e.target.value)}
                      className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                    />
                    <span className="font-mono text-sm text-white/70">{mutedColor}</span>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
            <div className="flex h-14">
              <div
                className="flex flex-1 items-center px-4"
                style={{ background: primaryColor }}
              >
                <span className="text-xs font-semibold text-white">
                  {brand?.company_name || "Your Company"}
                </span>
              </div>
              <div className="w-20" style={{ background: secondaryColor }} />
              <div className="w-16" style={{ background: accentColor }} />
            </div>
            {useCustomColors ? (
              <div
                className="flex items-center justify-between px-4 py-3 text-xs"
                style={{ background: backgroundColor, color: textColor }}
              >
                <span>Preview text color sample</span>
                <span style={{ color: mutedColor }}>Muted detail sample</span>
              </div>
            ) : null}
          </div>
        </SetupFieldGroup>
      </QuestionCard>

      <QuestionCard
        number={4}
        title="What fonts should your proposal use?"
        description="Choose separate heading and body fonts using either built-in options or uploaded custom fonts."
      >
        <div className="space-y-5">
          <div>
            <SetupLabel>Upload custom font</SetupLabel>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => fontUploadRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/20 hover:bg-white/10"
              >
                <Type className="h-4 w-4" />
                Upload .ttf or .otf
              </button>

              {uploadBrandFont.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-white/50" />
              ) : null}

              <input
                ref={fontUploadRef}
                type="file"
                accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleFontUpload(file);
                }}
              />
            </div>
          </div>

          <FontRolePicker
            role="heading"
            selection={headingFont}
            uploadedFonts={uploadedFonts}
            onSelectBuiltIn={handleSelectBuiltInFont}
            onSelectUploaded={handleSelectUploadedFont}
          />

          <FontRolePicker
            role="body"
            selection={bodyFont}
            uploadedFonts={uploadedFonts}
            onSelectBuiltIn={handleSelectBuiltInFont}
            onSelectUploaded={handleSelectUploadedFont}
          />
        </div>
      </QuestionCard>

      <BrandingSummaryCard
        showLogo={showLogo}
        useCustomColors={useCustomColors}
        headingFontLabel={headingFontLabel}
        bodyFontLabel={bodyFontLabel}
      />

      <SetupActions>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="flex items-center gap-2 rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save & continue
        </button>
      </SetupActions>
    </div>
  );
}